// built-from: /activity-contract-pool v1 impl:activity-contract-pool role:implementation
// 093: the process landscape — the cross-process picture, derived from the
// 089 register and glossary. Processes are nodes; an event one process
// raises and another reacts to is an edge; an event nobody reacts to, or an
// incoming event nobody raises, is a loose end on the node itself. Pure:
// layout is deterministic from the register order and the links.

export type LandscapeState = 'empty' | 'narrated' | 'walked' | 'agreed';

export type LandscapeImportance = 'core' | 'supporting' | 'generic';

export interface LandscapeProcess {
  processId: string;
  name: string;
  state: LandscapeState | string;
  stepCount: number;
  order: number;
  removedAt?: string | null;
  // 095: the business map's importance and storm rank, carried onto the
  // card and the table.
  importance?: LandscapeImportance | null;
  rank?: number | null;
  // 096: the business-map domain that owns the process; null is unplaced.
  domainId?: string | null;
}

export interface LandscapeDomain {
  id: string;
  name: string;
}

// The domain filter value that means "no domain yet".
export const UNPLACED_DOMAIN = '~unplaced';
export const UNPLACED_LABEL = 'Not placed yet';

// How a process's connections read on the card and in the table.
// `not-assessed`: its domain has not been walked, so the events are not
// known yet. `open`: an event it raises that nobody reacts to, or one it
// awaits that nothing raises. `reviewed`: walked, and every crossing
// event has a partner.
export type ConnectionStatus = 'not-assessed' | 'open' | 'reviewed';

export interface OpenQuestion {
  event: string;
  direction: 'out' | 'in';
  question: string;
}

export interface LandscapeGlossary {
  contexts?: Array<{ name: string; processIds: string[] }>;
  actors?: Array<{ name: string; kind?: string; processIds: string[] }>;
  systems?: Array<{ name: string; processIds: string[] }>;
  events?: Array<{ name: string; raisedIn: string[]; incomingIn: string[] }>;
}

export interface LandscapeNode {
  id: string;
  kind: 'process' | 'system';
  name: string;
  state: LandscapeState | string;
  stepCount: number;
  order: number;
  column: number;
  row: number;
  x: number;
  y: number;
  // Events this process raises that no process reacts to.
  raisedUnconsumed: string[];
  // Events this process reacts to that no process raises.
  incomingUnraised: string[];
  importance?: LandscapeImportance | null;
  rank?: number | null;
  domainId?: string | null;
}

export interface LandscapeEdge {
  id: string;
  from: string;
  to: string;
  events: string[];
  // A link that runs against the register order; drawn as a return curve.
  backward: boolean;
}

export interface LensEntry {
  kind: 'context' | 'actor' | 'system';
  name: string;
  processIds: string[];
}

export interface Landscape {
  nodes: LandscapeNode[];
  edges: LandscapeEdge[];
  lens: LensEntry[];
  width: number;
  height: number;
}

export const NODE_WIDTH = 240;
export const NODE_HEIGHT = 148;
export const COLUMN_GAP = 140;
export const ROW_GAP = 40;
export const PAD_X = 40;
export const PAD_Y = 32;

function humanise(name: string): string {
  const spaced = (name || '')
    .replace(/[-_]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim();
  if (!spaced) return name;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

export const humaniseEventName = humanise;

// One vocabulary for a process's state, wherever it shows.
const STATE_LABEL: Record<string, string> = {
  empty: 'No steps yet',
  narrated: 'Steps captured',
  walked: 'Detail mapping started',
  agreed: 'Agreed version available',
  system: 'external system',
};

export function stateLabel(state: string): string {
  return STATE_LABEL[state] ?? state;
}

export function buildLandscape(
  processes: LandscapeProcess[],
  glossary: LandscapeGlossary | null | undefined,
  options: { externalSystems?: boolean } = {},
): Landscape {
  const live = processes
    .filter((process) => !process.removedAt)
    .slice()
    .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
  const liveIds = new Set(live.map((process) => process.processId));
  const orderOf = new Map(live.map((process) => [process.processId, process.order]));
  const events = glossary?.events ?? [];

  // Edges: one per (raiser, reactor) pair, carrying every event that crosses it.
  const edgeMap = new Map<string, LandscapeEdge>();
  for (const event of events) {
    const raisers = (event.raisedIn || []).filter((id) => liveIds.has(id));
    const reactors = (event.incomingIn || []).filter((id) => liveIds.has(id));
    for (const from of raisers) {
      for (const to of reactors) {
        if (from === to) continue;
        const id = `${from}->${to}`;
        const edge = edgeMap.get(id) ?? {
          id, from, to, events: [], backward: (orderOf.get(from) ?? 0) > (orderOf.get(to) ?? 0),
        };
        if (!edge.events.includes(event.name)) edge.events.push(event.name);
        edgeMap.set(id, edge);
      }
    }
  }
  const edges = [...edgeMap.values()];

  // Loose ends per process.
  const raisedUnconsumed = new Map<string, string[]>();
  const incomingUnraised = new Map<string, string[]>();
  for (const event of events) {
    const raisers = (event.raisedIn || []).filter((id) => liveIds.has(id));
    const reactors = (event.incomingIn || []).filter((id) => liveIds.has(id));
    if (reactors.length === 0) {
      for (const id of raisers) raisedUnconsumed.set(id, [...(raisedUnconsumed.get(id) ?? []), event.name]);
    }
    if (raisers.length === 0) {
      for (const id of reactors) incomingUnraised.set(id, [...(incomingUnraised.get(id) ?? []), event.name]);
    }
  }

  // Layering: longest path over FORWARD links (lower register order to
  // higher). Backward links never change a column, so the layout is
  // deterministic and acyclic by construction.
  const layer = new Map<string, number>(live.map((process) => [process.processId, 0]));
  const forward = edges.filter((edge) => !edge.backward);
  for (const process of live) {
    const incoming = forward.filter((edge) => edge.to === process.processId);
    const depth = incoming.reduce((max, edge) => Math.max(max, (layer.get(edge.from) ?? 0) + 1), 0);
    layer.set(process.processId, depth);
  }

  const systemsOn = options.externalSystems === true;
  const systemNodes: LandscapeNode[] = systemsOn
    ? (glossary?.actors ?? [])
      .filter((actor) => actor.kind === 'externalSystem' && actor.processIds.some((id) => liveIds.has(id)))
      .map((actor, index) => ({
        id: `system:${actor.name}`,
        kind: 'system' as const,
        name: humanise(actor.name),
        state: 'system',
        stepCount: 0,
        order: -1,
        column: 0,
        row: index,
        x: 0,
        y: 0,
        raisedUnconsumed: [],
        incomingUnraised: [],
      }))
    : [];
  const columnOffset = systemNodes.length > 0 ? 1 : 0;

  const rowsPerColumn = new Map<number, number>();
  const nodes: LandscapeNode[] = live.map((process) => {
    const column = (layer.get(process.processId) ?? 0) + columnOffset;
    const row = rowsPerColumn.get(column) ?? 0;
    rowsPerColumn.set(column, row + 1);
    return {
      id: process.processId,
      kind: 'process',
      name: process.name,
      state: process.state,
      stepCount: process.stepCount,
      order: process.order,
      column,
      row,
      x: 0,
      y: 0,
      raisedUnconsumed: raisedUnconsumed.get(process.processId) ?? [],
      incomingUnraised: incomingUnraised.get(process.processId) ?? [],
      importance: process.importance ?? null,
      rank: process.rank ?? null,
      domainId: process.domainId ?? null,
    };
  });

  const systemEdges: LandscapeEdge[] = systemsOn
    ? (glossary?.actors ?? [])
      .filter((actor) => actor.kind === 'externalSystem')
      .flatMap((actor) => actor.processIds
        .filter((id) => liveIds.has(id))
        .map((id) => ({ id: `system:${actor.name}->${id}`, from: `system:${actor.name}`, to: id, events: [], backward: false })))
    : [];

  const all = [...systemNodes, ...nodes];
  for (const node of all) {
    node.x = PAD_X + node.column * (NODE_WIDTH + COLUMN_GAP);
    node.y = PAD_Y + node.row * (NODE_HEIGHT + ROW_GAP);
  }
  const width = all.length > 0 ? Math.max(...all.map((node) => node.x + NODE_WIDTH)) + PAD_X : PAD_X * 2;
  const height = all.length > 0 ? Math.max(...all.map((node) => node.y + NODE_HEIGHT)) + PAD_Y : PAD_Y * 2;

  const lens: LensEntry[] = [
    ...(glossary?.contexts ?? []).map((entry) => ({ kind: 'context' as const, name: entry.name, processIds: entry.processIds.filter((id) => liveIds.has(id)) })),
    ...(glossary?.actors ?? []).map((entry) => ({ kind: entry.kind === 'externalSystem' ? 'system' as const : 'actor' as const, name: entry.name, processIds: entry.processIds.filter((id) => liveIds.has(id)) })),
    ...(glossary?.systems ?? []).map((entry) => ({ kind: 'system' as const, name: entry.name, processIds: entry.processIds.filter((id) => liveIds.has(id)) })),
  ].filter((entry) => entry.processIds.length > 0);

  return { nodes: all, edges: [...systemEdges, ...edges], lens, width, height };
}

// The SVG path for a link: a cubic from the source's right edge to the
// target's left edge; a backward link dips below both cards and returns.
export function edgePath(from: LandscapeNode, to: LandscapeNode): string {
  const x1 = from.x + NODE_WIDTH;
  const y1 = from.y + NODE_HEIGHT / 2;
  const x2 = to.x;
  const y2 = to.y + NODE_HEIGHT / 2;
  if (x2 > x1) {
    const dx = Math.max(40, (x2 - x1) / 2);
    return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
  }
  const dip = Math.max(from.y, to.y) + NODE_HEIGHT + ROW_GAP / 2;
  const x1b = from.x + NODE_WIDTH;
  return `M ${x1b} ${y1} C ${x1b + 60} ${y1}, ${x1b + 60} ${dip}, ${(x1b + x2) / 2} ${dip} S ${x2 - 60} ${dip}, ${x2} ${y2}`;
}

// The midpoint of a link, for its label.
export function edgeLabelPoint(from: LandscapeNode, to: LandscapeNode): { x: number; y: number } {
  const x1 = from.x + NODE_WIDTH;
  const y1 = from.y + NODE_HEIGHT / 2;
  const x2 = to.x;
  const y2 = to.y + NODE_HEIGHT / 2;
  if (x2 > x1) return { x: (x1 + x2) / 2, y: (y1 + y2) / 2 };
  return { x: (x1 + x2) / 2, y: Math.max(from.y, to.y) + NODE_HEIGHT + ROW_GAP / 2 };
}

// The lens: node ids lit for a glossary entry (everything when none).
export function highlightFor(landscape: Landscape, selection: { kind: LensEntry['kind']; name: string } | null): Set<string> {
  if (!selection) return new Set(landscape.nodes.map((node) => node.id));
  const entry = landscape.lens.find((candidate) => candidate.kind === selection.kind && candidate.name === selection.name);
  const lit = new Set(entry?.processIds ?? []);
  if (selection.kind === 'system') lit.add(`system:${selection.name}`);
  return lit;
}

// How a process's connections read (see ConnectionStatus).
export function connectionStatus(node: Pick<LandscapeNode, 'state' | 'raisedUnconsumed' | 'incomingUnraised'>): ConnectionStatus {
  if (node.state === 'empty' || node.state === 'narrated') return 'not-assessed';
  if (node.raisedUnconsumed.length > 0 || node.incomingUnraised.length > 0) return 'open';
  return 'reviewed';
}

// The open questions on a process, in words a person would ask.
export function openQuestions(node: Pick<LandscapeNode, 'raisedUnconsumed' | 'incomingUnraised'>): OpenQuestion[] {
  return [
    ...node.raisedUnconsumed.map((event) => ({
      event, direction: 'out' as const, question: `Who reacts when "${humanise(event)}" is raised?`,
    })),
    ...node.incomingUnraised.map((event) => ({
      event, direction: 'in' as const, question: `What raises "${humanise(event)}" before it starts?`,
    })),
  ];
}

export interface LandscapeFilter {
  query?: string;
  // 096: "open questions only" (093 called it loose ends only).
  looseOnly?: boolean;
  // A business-map domain id, or UNPLACED_DOMAIN for processes with none.
  domain?: string;
}

// The filter: node ids shown for a name query, a domain and the
// open-questions toggle. System cards follow the processes they start.
export function visibleNodes(landscape: Landscape, filter: LandscapeFilter): Set<string> {
  const query = (filter.query || '').trim().toLowerCase();
  const domain = filter.domain || '';
  const shown = new Set<string>();
  for (const node of landscape.nodes) {
    if (node.kind !== 'process') continue;
    if (query && !node.name.toLowerCase().includes(query)) continue;
    if (domain && (domain === UNPLACED_DOMAIN ? Boolean(node.domainId) : node.domainId !== domain)) continue;
    if (filter.looseOnly && node.raisedUnconsumed.length === 0 && node.incomingUnraised.length === 0) continue;
    shown.add(node.id);
  }
  for (const node of landscape.nodes) {
    if (node.kind !== 'system') continue;
    if (landscape.edges.some((edge) => edge.from === node.id && shown.has(edge.to))) shown.add(node.id);
  }
  return shown;
}

// The neighbourhood of a node: itself, and everything one link away.
export function neighboursOf(landscape: Landscape, nodeId: string): Set<string> {
  const near = new Set([nodeId]);
  for (const edge of landscape.edges) {
    if (edge.from === nodeId) near.add(edge.to);
    if (edge.to === nodeId) near.add(edge.from);
  }
  return near;
}

export interface LandscapeRow {
  processId: string;
  name: string;
  state: string;
  stepCount: number;
  raises: number;
  reactsTo: number;
  looseEnds: number;
  status: ConnectionStatus;
  importance: LandscapeImportance | null;
  rank: number | null;
  domainId: string | null;
}

// One row per process for the table, in storm order then register order.
export function landscapeRows(landscape: Landscape): LandscapeRow[] {
  return landscape.nodes
    .filter((node) => node.kind === 'process')
    .slice()
    .sort((a, b) => stormOrder(a, b))
    .map((node) => ({
      processId: node.id,
      name: node.name,
      state: node.state,
      stepCount: node.stepCount,
      raises: landscape.edges.filter((edge) => edge.from === node.id).reduce((sum, edge) => sum + edge.events.length, 0),
      reactsTo: landscape.edges.filter((edge) => edge.to === node.id).reduce((sum, edge) => sum + edge.events.length, 0),
      looseEnds: node.raisedUnconsumed.length + node.incomingUnraised.length,
      status: connectionStatus(node),
      importance: node.importance ?? null,
      rank: node.rank ?? null,
      domainId: node.domainId ?? null,
    }));
}

// Storm order: ranked processes first by rank, then the rest in register
// order (095's rule, shared by the sidebar, the table and the map's rows).
export function stormOrder(
  a: { rank?: number | null; order: number },
  b: { rank?: number | null; order: number },
): number {
  const rankA = a.rank ?? null;
  const rankB = b.rank ?? null;
  if (rankA != null && rankB != null) return rankA - rankB;
  if (rankA != null) return -1;
  if (rankB != null) return 1;
  return a.order - b.order;
}

// "Storm next": the first process in storm order with no steps yet.
export function stormNextId(processes: Array<{ processId: string; stepCount: number; rank?: number | null; order: number; removedAt?: string | null }>): string | null {
  const live = processes.filter((process) => !process.removedAt).slice().sort(stormOrder);
  return live.find((process) => process.stepCount === 0)?.processId ?? null;
}

// Processes grouped under their business-map domains, in the map's order,
// with "Not placed yet" last; each group in storm order. Empty groups are
// dropped, so a domain with nothing in it takes no room.
export function groupByDomain<T extends { domainId?: string | null; rank?: number | null; order: number }>(
  processes: T[],
  domains: LandscapeDomain[],
): Array<{ id: string; name: string; processes: T[] }> {
  const sorted = processes.slice().sort(stormOrder);
  const groups = domains.map((domain) => ({
    id: domain.id,
    name: domain.name,
    processes: sorted.filter((process) => process.domainId === domain.id),
  }));
  const known = new Set(domains.map((domain) => domain.id));
  groups.push({
    id: UNPLACED_DOMAIN,
    name: UNPLACED_LABEL,
    processes: sorted.filter((process) => !process.domainId || !known.has(process.domainId)),
  });
  return groups.filter((group) => group.processes.length > 0);
}
